import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {AsaxHeaderService, HeaderFilter} from "asax-common";
import {SourceResolved, SourceResolverService} from "../source-resolver.service";
import {Observable, of, Subject} from "rxjs";
import {ElasticValidator, ElasticValidatorFactory} from "asax-elastic";
import {debounceTime, filter, mergeAll, switchMap, tap} from "rxjs/operators";
import {searchInputDebounceTime} from "../../search-input-text/search-input-text.component";
import {TextSearchInput} from "../../search-input.model";

@Component({
  selector: 'asax-header-search-input-source-resolver',
  templateUrl: './source-resolver-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourceResolverInputComponent implements OnInit, OnChanges {
  @ViewChild('tooltip', {static: false}) tooltip: NgbTooltip;

  @Input() searchInput: TextSearchInput;

  @Output() outFilter = new EventEmitter<HeaderFilter>();

  isInvalid: boolean;
  errorMessage: string;

  sourceResolved: SourceResolved;

  private valueSubject = new Subject<string>();
  private valueSubjectWithDebounceTime = new Subject<string>();
  private valueIsValidatedSubject = new Subject();

  private elasticValidator: ElasticValidator;
  private sourceRaDecRadius: any;

  constructor(private readonly elasticValidatorFactory: ElasticValidatorFactory,
              private readonly sourceResolverService: SourceResolverService,
              private readonly asaxHeaderService: AsaxHeaderService,
              private readonly changeDetectorRef: ChangeDetectorRef) {
    this.elasticValidator = this.elasticValidatorFactory.getElasticValidator('source-resolver');
  }

  ngOnInit(): void {
    this.valueIsValidatedSubject.asObservable().pipe(
      filter(() => this.searchInput?.value?.length > 0),
      filter(() => this.isInvalid === false),
      tap(() => this.tooltip?.open()),
      switchMap(() => this.resolveSource()),
      tap((sourceResolved: SourceResolved) => this.sourceResolved = sourceResolved),
      tap(() => this.tooltip?.close()),
      tap(() => this.validateSource()),
      tap(() => this.changeDetectorRef.markForCheck()),
      tap(() => this.buildSourceRaDecRadius()),
      tap(() => this.setSourceNameSearchRadius()),
      tap(() => this.emitFilter())
    ).subscribe();

    this.valueIsValidatedSubject.asObservable().pipe(
      filter(() => !this.searchInput?.value?.length),
      tap(() => this.emitFilter())
    ).subscribe();

    of(
      this.valueSubjectWithDebounceTime.asObservable().pipe(debounceTime(searchInputDebounceTime)),
      this.valueSubject.asObservable()
    ).pipe(
      mergeAll(),
      tap(() => this.isInvalid = undefined),
      tap(() => this.sourceResolved = undefined),
      tap(() => this.sourceRaDecRadius = undefined),
      tap(() => this.validateValue()),
      tap(() => this.changeDetectorRef.markForCheck()),
      tap(() => this.valueIsValidatedSubject.next(undefined))
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.valueChanged();
  }

  valueChangedWithDebounceTime(): void {
    this.valueSubjectWithDebounceTime.next(undefined);
  }

  clearSearchInputValue(): void {
    this.searchInput.value = undefined;
    this.valueChanged();
  }

  onExampleClick(example: string): void {
    this.searchInput.value = example;
    this.valueChanged();
  }

  private valueChanged(): void {
    this.valueSubject.next(undefined);
  }

  private validateValue(): void {
    const {isInvalid, errorMessage} = this.elasticValidator?.validate(this.searchInput.value);

    this.isInvalid = isInvalid;
    this.errorMessage = errorMessage;
  }

  private validateSource(): void {
    const isSourceResolvedValid = this.sourceResolved?.oName?.length > 0;

    if (isSourceResolvedValid) {
      this.isInvalid = false;
    } else {
      this.isInvalid = true;
      this.errorMessage = 'Source not found';
    }
  }

  private resolveSource(): Observable<any> {
    return this.sourceResolverService.resolveSource(this.searchInput.value);
  }

  private setSourceNameSearchRadius(): void {
    this.asaxHeaderService.setSourceNameSearchRadius(this.sourceRaDecRadius);
  }

  private buildSourceRaDecRadius() {
    if (this.searchInput.value?.length > 0 && this.isInvalid === false) {
      const {jRaDeg, jDecDeg, radius} = this.sourceResolved;
      this.sourceRaDecRadius = {ra: jRaDeg, dec: jDecDeg, radius};
    }
  }

  private emitFilter(): void {
    const filter = {
      id: this.searchInput.id,
      value: this.searchInput.value,
      valueParsed: this.sourceRaDecRadius,
      valueToDisplay: this.searchInput.value,
      label: this.searchInput.label,
      isDisabled: this.searchInput.isDisabled,
      isInvalid: this.isInvalid,
      elasticParserType: this.searchInput.elasticParserType
    }

    this.outFilter.emit(filter);
  }
}
